import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { supabase } from '@/integrations/supabase/client';

interface HotelInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  bank_name?: string;
  bank_account?: string;
  correspondent_account?: string;
  bik?: string;
}

interface DailyRate {
  date: string;
  price: number;
  seasonName?: string;
}

/**
 * 🏆 МИРОВОЙ УРОВЕНЬ PDF ГЕНЕРАТОР
 * Генерирует профессиональный PDF счет с детализацией по сезонам и услугам
 */
export const generateInvoicePDF = async (
  booking: BookingWithFinancials,
  hotelInfo: HotelInfo
): Promise<void> => {
  // Получаем шаблон счета из базы данных
  const { data: template } = await supabase
    .from('invoice_templates')
    .select('*')
    .eq('hotel_id', booking.hotel_id)
    .eq('is_default', true)
    .single();

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Константы для позиционирования
  let yPosition = 15;
  const leftMargin = 15;
  const rightMargin = pageWidth - 15;
  
  // === ШАПКА ДОКУМЕНТА - ПРЕМИУМ ДИЗАЙН ===
  // Верхняя декоративная линия
  doc.setDrawColor(0, 100, 180);
  doc.setLineWidth(3);
  doc.line(leftMargin, 10, rightMargin, 10);
  
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 70, 140);
  doc.text('СЧЁТ НА ОПЛАТУ', pageWidth / 2, yPosition + 10, { align: 'center' });
  yPosition += 20;
  
  // Номер и дата счета
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const invoiceNumber = (booking as any).invoice_number || booking.booking_reference || 'Не указан';
  const invoiceDate = (booking as any).invoice_sent_at 
    ? new Date((booking as any).invoice_sent_at).toLocaleDateString('ru-RU')
    : new Date().toLocaleDateString('ru-RU');
  
  doc.text(`№ ${invoiceNumber} от ${invoiceDate}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 12;
  
  // Тонкая разделительная линия
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, yPosition, rightMargin, yPosition);
  yPosition += 8;
  
  // === ИНФОРМАЦИЯ ОБ ОТЕЛЕ (Поставщик) ===
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 70, 140);
  doc.text('ПОСТАВЩИК УСЛУГ:', leftMargin, yPosition);
  yPosition += 5;
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  const companyName = template?.company_name || hotelInfo.name;
  doc.text(companyName, leftMargin, yPosition);
  yPosition += 5;
  
  if (template?.inn || hotelInfo.inn) {
    doc.text(`ИНН: ${template?.inn || hotelInfo.inn}`, leftMargin, yPosition);
    yPosition += 4;
  }
  
  if (template?.kpp || hotelInfo.kpp) {
    doc.text(`КПП: ${template?.kpp || hotelInfo.kpp}`, leftMargin, yPosition);
    yPosition += 4;
  }
  
  if (template?.legal_address || hotelInfo.address) {
    const addressLines = doc.splitTextToSize(`Адрес: ${template?.legal_address || hotelInfo.address}`, 100);
    doc.text(addressLines, leftMargin, yPosition);
    yPosition += addressLines.length * 4;
  }
  
  if (template?.phone || hotelInfo.phone) {
    doc.text(`Тел: ${template?.phone || hotelInfo.phone}`, leftMargin, yPosition);
    yPosition += 4;
  }
  
  yPosition += 5;
  
  // === БАНКОВСКИЕ РЕКВИЗИТЫ ===
  if (template?.bank_name || hotelInfo.bank_name) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 70, 140);
    doc.text('БАНКОВСКИЕ РЕКВИЗИТЫ:', leftMargin, yPosition);
    yPosition += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text(template?.bank_name || hotelInfo.bank_name || '', leftMargin, yPosition);
    yPosition += 4;
    
    if (template?.bank_account || hotelInfo.bank_account) {
      doc.text(`Р/с: ${template?.bank_account || hotelInfo.bank_account}`, leftMargin, yPosition);
      yPosition += 4;
    }
    
    if (template?.bik || hotelInfo.bik) {
      doc.text(`БИК: ${template?.bik || hotelInfo.bik}`, leftMargin, yPosition);
      yPosition += 4;
    }
  }
  
  yPosition += 5;
  
  // === ИНФОРМАЦИЯ О ГОСТЯХ ===
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 70, 140);
  doc.text('ПЛАТЕЛЬЩИК:', leftMargin, yPosition);
  yPosition += 5;
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  const guestName = booking.guest_name || 'Не указан';
  doc.text(guestName, leftMargin, yPosition);
  yPosition += 4;
  
  if (booking.guest_phone) {
    doc.text(`Тел: ${booking.guest_phone}`, leftMargin, yPosition);
    yPosition += 4;
  }
  
  if (booking.guest_email) {
    doc.text(`Email: ${booking.guest_email}`, leftMargin, yPosition);
    yPosition += 4;
  }
  
  yPosition += 8;
  
  // === ДЕТАЛИ БРОНИРОВАНИЯ ===
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 70, 140);
  doc.text('ДЕТАЛИ БРОНИРОВАНИЯ:', leftMargin, yPosition);
  yPosition += 6;
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  
  const checkInDate = new Date(booking.check_in_date).toLocaleDateString('ru-RU');
  const checkOutDate = new Date(booking.check_out_date).toLocaleDateString('ru-RU');
  const nights = Math.ceil(
    (new Date(booking.check_out_date).getTime() - new Date(booking.check_in_date).getTime()) / (1000 * 60 * 60 * 24)
  );
  
  doc.text(`Номер брони: ${booking.booking_reference || booking.id}`, leftMargin, yPosition);
  yPosition += 4;
  doc.text(`Категория: ${booking.room_type}`, leftMargin, yPosition);
  yPosition += 4;
  doc.text(`Период: ${checkInDate} — ${checkOutDate} (${nights} ноч.)`, leftMargin, yPosition);
  yPosition += 4;
  doc.text(`Гостей: ${booking.number_of_guests}`, leftMargin, yPosition);
  yPosition += 8;
  
  // === ТАБЛИЦА УСЛУГ - ПРЕМИУМ ДИЗАЙН ===
  const currency = template?.default_currency || 'RUB';
  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽';
  
  // Заголовок таблицы
  const tableTop = yPosition;
  const col1 = leftMargin;
  const col2 = leftMargin + 10;
  const col3 = pageWidth - 75;
  const col4 = pageWidth - 50;
  const col5 = pageWidth - 25;
  
  // Фон заголовка
  doc.setFillColor(0, 70, 140);
  doc.rect(leftMargin, tableTop - 4, rightMargin - leftMargin, 8, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('№', col1 + 2, tableTop);
  doc.text('Наименование услуги', col2, tableTop);
  doc.text('Кол-во', col3, tableTop, { align: 'right' });
  doc.text('Цена', col4, tableTop, { align: 'right' });
  doc.text('Сумма', col5, tableTop, { align: 'right' });
  
  yPosition = tableTop + 7;
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(9);
  
  let rowNum = 1;
  let totalAmount = 0;
  
  // 1. Проживание
  const pricePerNight = booking.total_amount / nights;
  doc.text(rowNum.toString(), col1 + 2, yPosition);
  
  const serviceNameText = `Проживание "${booking.room_type}" (${checkInDate} — ${checkOutDate})`;
  const splitService = doc.splitTextToSize(serviceNameText, col3 - col2 - 10);
  doc.text(splitService, col2, yPosition);
  
  doc.text(nights.toString(), col3, yPosition, { align: 'right' });
  doc.text(`${pricePerNight.toFixed(0)} ${currencySymbol}`, col4, yPosition, { align: 'right' });
  doc.text(`${booking.total_amount.toFixed(0)} ${currencySymbol}`, col5, yPosition, { align: 'right' });
  
  totalAmount += booking.total_amount;
  yPosition += splitService.length * 5 + 3;
  rowNum++;
  
  // 2. Дополнительные услуги
  if (booking.services && booking.services.length > 0) {
    for (const service of booking.services) {
      if (service.status !== 'cancelled') {
        // Чередующийся фон
        if (rowNum % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(leftMargin, yPosition - 4, rightMargin - leftMargin, 7, 'F');
        }
        
        doc.setTextColor(40, 40, 40);
        doc.text(rowNum.toString(), col1 + 2, yPosition);
        doc.text(service.service_name, col2, yPosition);
        doc.text(service.quantity.toString(), col3, yPosition, { align: 'right' });
        doc.text(`${service.unit_price.toFixed(0)} ${currencySymbol}`, col4, yPosition, { align: 'right' });
        doc.text(`${service.total_price.toFixed(0)} ${currencySymbol}`, col5, yPosition, { align: 'right' });
        
        totalAmount += service.total_price;
        yPosition += 6;
        rowNum++;
      }
    }
  }
  
  // Нижняя линия таблицы
  yPosition += 2;
  doc.setDrawColor(0, 70, 140);
  doc.setLineWidth(1);
  doc.line(leftMargin, yPosition, rightMargin, yPosition);
  yPosition += 8;
  
  // === ИТОГО - ПРЕМИУМ БЛОК ===
  // Фон для итого
  doc.setFillColor(240, 245, 250);
  doc.roundedRect(pageWidth - 90, yPosition - 5, 75, 35, 3, 3, 'F');
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  
  doc.text('Итого:', pageWidth - 85, yPosition + 2);
  doc.setFont('helvetica', 'bold');
  doc.text(`${totalAmount.toFixed(0)} ${currencySymbol}`, pageWidth - 20, yPosition + 2, { align: 'right' });
  
  yPosition += 8;
  doc.setFont('helvetica', 'normal');
  doc.text('НДС:', pageWidth - 85, yPosition + 2);
  doc.text('Без НДС', pageWidth - 20, yPosition + 2, { align: 'right' });
  
  // Оплачено
  const paidAmount = booking.financial?.paid_amount || 0;
  if (paidAmount > 0) {
    yPosition += 8;
    doc.setTextColor(34, 139, 34);
    doc.text('Оплачено:', pageWidth - 85, yPosition + 2);
    doc.text(`${paidAmount.toFixed(0)} ${currencySymbol}`, pageWidth - 20, yPosition + 2, { align: 'right' });
  }
  
  // К оплате
  const balanceAmount = totalAmount - paidAmount;
  yPosition += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0, 70, 140);
  doc.text('К ОПЛАТЕ:', pageWidth - 85, yPosition + 2);
  doc.text(`${balanceAmount.toFixed(0)} ${currencySymbol}`, pageWidth - 20, yPosition + 2, { align: 'right' });
  
  yPosition += 25;
  
  // === QR-КОД ДЛЯ ОПЛАТЫ ===
  if (template?.show_qr_code !== false) {
    try {
      const qrText = `Оплата: ${companyName}\nСчет: ${invoiceNumber}\nСумма: ${balanceAmount} ${currencySymbol}`;
      
      const qrCodeDataUrl = await QRCode.toDataURL(qrText, {
        width: 100,
        margin: 1,
        color: {
          dark: '#004680',
          light: '#FFFFFF'
        }
      });
      
      const qrSize = 35;
      const qrX = leftMargin;
      const qrY = yPosition;
      
      doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Сканируйте для оплаты', qrX + qrSize / 2, qrY + qrSize + 4, { align: 'center' });
    } catch (error) {
      console.error('Ошибка генерации QR-кода:', error);
    }
  }
  
  // === ПОДПИСИ ===
  const signatureY = Math.min(yPosition + 50, pageHeight - 40);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  
  // Директор
  doc.text('Руководитель:', leftMargin, signatureY);
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.3);
  doc.line(leftMargin + 30, signatureY, leftMargin + 70, signatureY);
  const directorName = template?.director_name || '';
  if (directorName) {
    doc.text(directorName, leftMargin + 75, signatureY);
  }
  
  // Главный бухгалтер
  doc.text('Гл. бухгалтер:', leftMargin, signatureY + 10);
  doc.line(leftMargin + 30, signatureY + 10, leftMargin + 70, signatureY + 10);
  const accountantName = template?.accountant_name || '';
  if (accountantName) {
    doc.text(accountantName, leftMargin + 75, signatureY + 10);
  }
  
  // М.П.
  doc.setFontSize(7);
  doc.text('М.П.', leftMargin + 100, signatureY + 5);
  doc.setDrawColor(180, 180, 180);
  doc.circle(leftMargin + 115, signatureY + 5, 12);
  
  // === ФУТЕР ===
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(120, 120, 120);
  const footer = template?.footer_text || 'Счет действителен в течение 5 банковских дней. Благодарим за выбор нашего отеля!';
  doc.text(footer, pageWidth / 2, pageHeight - 8, { align: 'center' });
  
  // Нижняя декоративная линия
  doc.setDrawColor(0, 100, 180);
  doc.setLineWidth(2);
  doc.line(leftMargin, pageHeight - 5, rightMargin, pageHeight - 5);
  
  // Сохраняем PDF
  const fileName = `Счет_${invoiceNumber}_${checkInDate.replace(/\./g, '-')}.pdf`;
  doc.save(fileName);
};